import {
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import {
  MatTableDataSource,
  MatSort,
  MatPaginator,
  PageEvent,
  MatTable,
  Sort,
} from "@angular/material";
import * as _ from "underscore";
import Utils from "../../services/utils";
import { DataService } from "../../services/data.service";
import { LoadingSpinnerService } from "../loading-spinner/loading-spinner.service";
import { DownloadService } from "../../services/download.service";
import { DownloadStatus } from "../../models/download.model";
import { ToastService } from "../toast/toast.service";
import { Router } from "@angular/router";
import { BatchDownloadModalComponent } from "../modal/modals/batch-download-modal.component";
import { GridFilterComponent } from "./grid-filter/grid-filter.component";
import { GridPaginatorComponent } from "./grid-paginator/grid-paginator.component";

@Component({
  templateUrl: "./grid.component.html",
  styleUrls: ["./grid.component.scss"],
  selector: "lowy-grid",
  providers: [DataService, DownloadService],
})
export class LowyGridComponent {
  public dataSource: MatTableDataSource<any>;
  public columns: Array<string>;
  public columnHeaders: Array<string>;
  public pageSizeOptions: Array<number>;
  public noResults: boolean;
  public columnsToTitleCase: string[];

  // we end up with index 0 being the selectAll checkbox, and the rows starting at i=1, see html
  checkboxes: Array<{
    isChecked: boolean;
    datum: any;
  }>;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("downloadUrlLink") downloadUrlLink: ElementRef;
  @ViewChild("batchDownload") batchDownload: BatchDownloadModalComponent;
  @ViewChild("gridFilter") gridFilter: GridFilterComponent;

  @Input() allowDownload: boolean;
  @Input() allowEmailDownload: boolean;
  @Input() allowRawDownload: boolean;
  @Input() allowFilter: boolean;

  @Input() isFromApprovalPage: boolean;
  @Input() dataType: string;

  @Input() buttonText: string;
  @Input() buttonText2: string;
  @Input() disableButtonIfEmpty: boolean;
  @Input() disableButtonIfEmpty2: boolean;

  @Input() showFromDate: boolean;
  public fromDate: string;
  @Input() showToDate: boolean;
  public toDate: string;
  @Output() fromDateChange: EventEmitter<string>;
  @Output() toDateChange: EventEmitter<string>;

  @Output() requestRefresh: EventEmitter<null>;
  @Output() buttonClick: EventEmitter<null>;
  @Output() buttonClick2: EventEmitter<null>;

  @Input() allowEdit: boolean;
  @Input() allowDelete: boolean;
  @Input() allowResubmit: boolean;
  @Input() allowAdjudicate: boolean;
  @Input() allowComment: boolean;
  @Input() allowGrading: boolean;
  @Input() allowToggle: boolean;
  @Input() toggleText: string;
  @Input() allowReason: string;
  public toggleCheckbox: boolean;

  @Output() edit: EventEmitter<any>;
  @Output() delete: EventEmitter<any>;
  @Output() resubmit: EventEmitter<any>;
  @Output() adjudicate: EventEmitter<any>;
  @Output() comment: EventEmitter<any>;
  @Output() grade: EventEmitter<any>;
  @Output() toggleToggled: EventEmitter<any>;
  @Output() reasonClick: EventEmitter<string>;

  @Input() allowCustomAction: boolean;
  @Input() customActionText: string;
  @Output() customAction: EventEmitter<any>;

  @Input() enableColumnFilters: boolean;
  public columnFilterValues: string[];
  public selectedColumnFilterValues: string[];
  public allData: Array<any>;
  public filterColumnHeaderIndex: number;
  public showFilterView: boolean;
  public filterValues: { [column: string]: string[] };
  @Output() filtersUpdated: EventEmitter<any>;
  @Output() sortUpdated: EventEmitter<Sort>;

  public activeFilters: { [column: string]: string[] } = {};

  @Output() public pageChange: EventEmitter<PageEvent>;
  public totalRowCount: number;
  @ViewChild(MatTable) table: MatTable<any>;

  @Input() useCustomPaginator = false;
  @Output() downloadAll = new EventEmitter();
  @ViewChild("gridFilter") gridFilterComponent: GridFilterComponent;
  private firstLoad = true;
  @ViewChild("customPaginator") customPaginator: GridPaginatorComponent;

  constructor(
    private dataService: DataService,
    private downloadService: DownloadService,
    private toastService: ToastService,
    private router: Router,
    private loadingSpinnerService: LoadingSpinnerService,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.requestRefresh = new EventEmitter();
    this.buttonClick = new EventEmitter();
    this.buttonClick2 = new EventEmitter();

    this.edit = new EventEmitter();
    this.delete = new EventEmitter();
    this.resubmit = new EventEmitter();
    this.adjudicate = new EventEmitter();
    this.comment = new EventEmitter();
    this.grade = new EventEmitter();
    this.toggleToggled = new EventEmitter();
    this.reasonClick = new EventEmitter();

    this.fromDateChange = new EventEmitter();
    this.toDateChange = new EventEmitter();

    this.customAction = new EventEmitter();
    this.filtersUpdated = new EventEmitter();
    this.sortUpdated = new EventEmitter();

    this.pageChange = new EventEmitter();
    this.downloadAll = new EventEmitter();

    this.columnsToTitleCase = [
      "decision",
      "status",
      "CF-FFA",
      "OCT",
      "FAFGRADE",
    ];
  }

  public async dataReady(
    data: Array<any>,
    columns: Array<string>,
    columnHeaders: Array<string>,
    filterValues?: { [column: string]: string[] },
    totalRowCount?: number
  ) {
    this.totalRowCount = totalRowCount;

    if (
      !columns.includes("actions") &&
      (this.allowEdit ||
        this.allowDelete ||
        this.allowResubmit ||
        this.allowAdjudicate ||
        this.allowComment ||
        this.allowGrading ||
        this.allowCustomAction)
    ) {
      columns.push("actions");
      columnHeaders.push("Actions");
    }

    this.dataSource = new MatTableDataSource();
    this.dataSource.paginator = this.paginator;
    // fix date sorting for matGrid https://stackoverflow.com/questions/49603499/how-to-sorting-by-date-string-with-mat-sort-header
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case "uploadDate":
          return new Date(item.uploadDate);
        case "fileName":
          if (item[property] && typeof item[property] === "string") {
            return item[property].toLowerCase();
          } else {
            return item[property];
          }
        default:
          return item[property];
      }
    };
    if (!this.useCustomPaginator) {
      this.dataSource.sort = this.sort;
    }
    this.columns = columns;
    this.columnHeaders = columnHeaders;
    this.filterValues = filterValues;

    this.checkboxes = [];
    this.checkboxes.push({
      isChecked: false,
      datum: "selectAll",
    });

    _.each(data, (d) => {
      this.checkboxes.push({
        isChecked: false,
        datum: d,
      });
    });
    this.dataSource.data = data;
    this.allData = data;

    this.pageSizeOptions = [5, 10, 25, 100];

    if (!data || data.length === 0) {
      this.noResults = true;
    } else {
      this.noResults = false;
    }

    if (this.useCustomPaginator) {
      // if (this.firstLoad) {
      //   this.sort.sortChange.subscribe((s: Sort) => {
      //     this.sortUpdated.emit(s);
      //   });
      // }
      // this.firstLoad = false;
    }
  }

  goto(row) {
    if (this.dataType === "any") {
      if (row["patientId"]) {
        this.router.navigate([
          "/data",
          row["datatypeAbbr"],
          row[row["datatypeAbbr"] + "Id"],
        ]);
      }
    } else if (this.dataType && row[this.dataType + "Id"]) {
      this.router.navigate(["/data", this.dataType, row[this.dataType + "Id"]]);
    } else if (this.dataType && row["_id"]) {
      this.router.navigate(["/data", this.dataType, row["_id"]]);
    }
  }

  selectAll() {
    let isAnUnchecked = false;
    // If there's at least one unchecked box, check everything
    // If everything is checked, uncheck everything
    for (let i = 0; i < this.checkboxes.length; i++) {
      if (!this.checkboxes[i].isChecked) {
        isAnUnchecked = true;
        break;
      }
    }

    for (let i = 0; i < this.checkboxes.length; i++) {
      this.checkboxes[i].isChecked = isAnUnchecked;
    }
  }

  checked(i: number) {
    this.checkboxes[i].isChecked = !this.checkboxes[i].isChecked;

    let isAnUnchecked;
    // starting at 1 because index 0 is for the selectAll checkbox
    for (let j = 1; j < this.checkboxes.length; j++) {
      if (!this.checkboxes[j].isChecked) {
        isAnUnchecked = true;
        break;
      }
    }

    // If everything is checked, check the selecAll box
    this.checkboxes[0].isChecked = !isAnUnchecked;
  }

  public checkedItems() {
    let itemsForDownload = [];
    if (!this.checkboxes) {
      return itemsForDownload;
    }
    // if 'select all' is checked, assume user wants to download ALL (obey filter), not just this page
    if (this.checkboxes[0].isChecked) {
      itemsForDownload = this.dataSource.filteredData;
    } else {
      for (let i = 1; i < this.checkboxes.length; i++) {
        // index 0 is 'select all' checkbox so start at 1
        if (this.checkboxes[i].isChecked) {
          // we get renderData because it's what the user sees, offset by 1 because data is index 0 but checkboxes are index 1
          const item = this.dataSource["_renderData"]._value[i - 1];
          if (item != null) {
            itemsForDownload.push(item);
          }
        }
      }
    }
    return itemsForDownload;
  }

  public async downloadFiles(downloadType: string, allItems?: any) {
    let itemsForDownload;

    if (this.checkboxes[0].isChecked) {
      if (allItems) {
        itemsForDownload = allItems;
      } else {
        this.downloadAll.emit(downloadType);
        return;
      }
    } else {
      itemsForDownload = JSON.parse(JSON.stringify(this.checkedItems()));
    }

    // input is either 'raw' or 'list'
    const formattedItemsForSpreadsheet =
      this.formatItemsForSpreadsheet(itemsForDownload);
    if (downloadType === "raw") {
      this.loadingSpinnerService.start();

      try {
        // to support legacy service side needs
        _.map(itemsForDownload, (item) => {
          if (this.dataType && item["datatypeAbbr"]) {
            item["dataType"] = item["datatypeAbbr"];
          } else {
            item["dataType"] = this.dataType;
          }
          return item;
        });

        let downloadStatus = await this.downloadService.requestBatchDownload(
          itemsForDownload,
          formattedItemsForSpreadsheet,
          this.isFromApprovalPage,
          this.batchDownload.emailDownloadLink
        );
        if (this.batchDownload.emailDownloadLink) {
          this.loadingSpinnerService.stop();
          this.toastService.throwToast(
            "You will receive an email when the download is ready."
          );
          return;
        }
        let downloadTimer = 100;
        while (downloadStatus.status === DownloadStatus.pending) {
          downloadStatus = await this.downloadService.getDownloadStatus(
            downloadStatus
          );
          await Utils.sleep(downloadTimer);
          if (downloadTimer < 5000) {
            downloadTimer += downloadTimer;
          }
        }
        if (downloadStatus.status === DownloadStatus.error) {
          this.loadingSpinnerService.stop();
          if (
            downloadStatus.errorMessage === "The specified key does not exist."
          ) {
            if (this.isFromApprovalPage) {
              this.toastService.throwToast(
                "The file has already been approved/rejected."
              );
              this.requestRefresh.emit();
            } else {
              this.toastService.throwToast(downloadStatus.errorMessage);
            }
          } else {
            this.toastService.throwToast(downloadStatus.errorMessage);
          }
          return;
        }
        if (downloadStatus.url) {
          this.loadingSpinnerService.stop();
          this.downloadUrlLink.nativeElement.href = downloadStatus.url;
          this.downloadUrlLink.nativeElement.click();
          return;
        } else {
          this.loadingSpinnerService.stop();
          this.toastService.throwToast("Unknown error");
          return;
        }
      } catch (err) {
        this.loadingSpinnerService.stop();
        this.toastService.throwToast(err);
      }
    } else {
      try {
        const signedUrl = await this.dataService.createSpreadsheet(
          formattedItemsForSpreadsheet
        );
        this.downloadUrlLink.nativeElement.href = signedUrl;
        this.downloadUrlLink.nativeElement.click();
      } catch (err) {
        this.toastService.throwToast(err);
      }
    }
  }

  private formatItemsForSpreadsheet(itemsForDownload: Array<any>): Array<any> {
    for (let i = 0; i < itemsForDownload.length; i++) {
      delete itemsForDownload[i].checkbox;
      delete itemsForDownload[i].hasFamily;
      delete itemsForDownload[i].iconType;
      delete itemsForDownload[i].dataType;
    }
    const formattedItemsForDownload = [];
    _.each(itemsForDownload, (oldItem) => {
      const newItem = {};
      for (let i = 0; i < this.columns.length; i++) {
        newItem[this.columnHeaders[i]] = oldItem[this.columns[i]];
      }
      formattedItemsForDownload.push(newItem);
    });

    // for patient csv add row for coded columnIds above friendly names
    if (this.dataType === "patient") {
      const codedColumnIds = {};
      for (let i = 0; i < this.columns.length; i++) {
        if (
          !["checkbox", "hasFamily", "iconType", "dataType"].includes(
            this.columns[i]
          )
        ) {
          codedColumnIds[this.columnHeaders[i]] = this.columns[i];
        }
      }
      formattedItemsForDownload.unshift(codedColumnIds);
    }

    return formattedItemsForDownload;
  }

  // when the filter is updated, we probably need to update the select all, just call it twice
  public async filterUpdated() {
    // if selectAll is checked, toggle it off/on
    if (this.checkboxes[0]) {
      this.selectAll();
      await Utils.sleep();
      this.selectAll();
    }
  }

  public applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
    this.dataSource.filter = filterValue;
    this.filterUpdated();
  }

  public isDateColumn(column: string) {
    if (
      [
        "createdAt",
        "updatedAt",
        "photoDate",
        "uploadDate",
        "submittedDate",
        "imageDate",
      ].includes(column)
    ) {
      return true;
    } else {
      return false;
    }
  }

  public clearDates() {
    this.fromDate = null;
    this.toDate = null;
  }

  public showFilters(e, columnHeaderIndex: number) {
    e.stopPropagation();
    this.columns;
    this.columnHeaders;
    this.dataSource.data;

    this.filterColumnHeaderIndex = columnHeaderIndex;
    this.columnFilterValues =
      this.filterValues[this.columns[columnHeaderIndex]];
    this.selectedColumnFilterValues = this.activeFilters[columnHeaderIndex];

    this.showFilterView = true;
  }

  public applyFilters(filterValues: string[]) {
    this.showFilterView = false;
    this.activeFilters[this.filterColumnHeaderIndex] = filterValues;

    const allFilters = [];
    _.each(this.activeFilters, (filters, idx) => {
      if (filters && filters.length > 0) {
        const sheetInfo = this.columns[idx].split(":");
        allFilters.push({
          spreadsheetId: sheetInfo[0],
          columnId: sheetInfo[1],
          values: filters,
        });
      }
    });

    this.filtersUpdated.emit(allFilters);
  }

  public clearFilter(filter: {
    spreadsheetId?: string;
    columnId: string;
    values: string[];
  }) {
    let col = filter.columnId;
    if (filter.spreadsheetId === "visitId") {
      col = filter.spreadsheetId;
    } else if (filter.spreadsheetId) {
      col = filter.spreadsheetId + ":" + filter.columnId;
    }
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i] === col) {
        delete this.activeFilters[i];
      }
    }
  }

  public clearAllFilters() {
    this.activeFilters = {};
    this.customPaginator.reset();
  }

  public getCountForBatchDownload(): number {
    if (this.checkboxes && this.checkboxes[0] && this.checkboxes[0].isChecked) {
      return this.totalRowCount;
    } else {
      return this.checkedItems().length;
    }
  }

  public updatePage(e): void {
    const gridHolder = document.getElementById("gridHolder");
    if (gridHolder) {
      gridHolder.scrollIntoView({ behavior: "smooth" });
    }
    this.pageChange.emit(e);
  }

  resetPagination() {
    this.customPaginator.reset();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/grid/grid.component.ts